/**
 * confidence.js
 * -----------------------------------------------------------------------------
 * Tri-state coverage model for the zone Land-characteristics feature.
 *
 * A farmer draws a field polygon ("zone"). We intersect it with UK land datasets.
 * CATEGORICAL layers (NVZ, SSSI/SAC/SPA designations, flood zones) get a plain
 * tri-state ('none' | 'partial' | 'full') from how much of the zone the feature
 * covers, produced by `deriveTriState`. The UI maps those to per-category words.
 *
 * NOTE: a per-layer High/Medium/Low *confidence* rating (rateConfidence) and its
 * cartographic thresholds used to live here, feeding a "confidence pill" in the
 * Land characteristics panel. That pill was dropped from the UI (BBB-456), so the
 * rating model and its thresholds have been removed — only the tri-state remains.
 */

export const TRI_STATE_THRESHOLDS = Object.freeze({
  /**
   * coveredFraction (0..1) → 'none' | 'partial' | 'full'.
   *  ≥ FULL    → 'full'    (treat near-total coverage as full; absorbs rounding).
   *  > EPSILON → 'partial' (small epsilon absorbs sliver-intersection noise from
   *              polygon overlay — a 0.3% clip on a boundary is not "partly within").
   *  else      → 'none'.
   */
  FULL: 0.95,
  EPSILON: 0.01,
});

/**
 * deriveTriState — generic tri-state for CATEGORICAL layers from coverage alone.
 * The UI maps 'none'|'partial'|'full' to per-category words
 * (e.g. "not designated / partly within / within").
 *
 * @param {number} coveredFraction 0..1 fraction of the zone intersected by the feature
 * @returns {'none'|'partial'|'full'}
 *
 * Uses EPSILON to absorb sliver-intersection noise: a tiny boundary clip is 'none',
 * not 'partial'. Near-total coverage rounds up to 'full'.
 */
export function deriveTriState(coveredFraction) {
  const covered = typeof coveredFraction === 'number' && coveredFraction > 0 ? coveredFraction : 0;
  if (covered >= TRI_STATE_THRESHOLDS.FULL) return 'full';
  if (covered > TRI_STATE_THRESHOLDS.EPSILON) return 'partial';
  return 'none';
}
